import React, { useState, useEffect, useCallback } from "react";
import { Form, Row, Col, Button, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';
import { useHasPermission, PERMISSIONS } from "../../../utils/permissions";
import PermissionGate from "../../common/PermissionGate";

const customStyles = {
  rows: {
    style: {
      minHeight: '80px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingRight: '8px',
    },
  },
  table: {
    style: {
      minWidth: '100%',
    },
  },
};

export default function PMQualityCheckTab({ pmPurchaseOrderId, order, onUpdate }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [qualityForm, setQualityForm] = useState({
    grnId: "",
    packingMaterial: "",
    quantity: 0,
    unit: "",
    qualityStatus: "Approved",
    qualityRemarks: "",
    rejectionReason: ""
  });

  const canAddQuality = useHasPermission(PERMISSIONS.QUALITY_CHECK_ADD);
  
    const fetchQualityData = useCallback(async () => {
      try {
        const [grnsRes, qualityRes] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/pm-grn?pmPurchaseOrder=${pmPurchaseOrderId}`),
          axios.get(`${config.API_BASE_URL}/pm-quality-checks?pmPurchaseOrder=${pmPurchaseOrderId}`)
        ]);
        setGrns(grnsRes.data || []);
        setQualityEntries(qualityRes.data || []);
      } catch (err) {
        console.error("Fetch quality data error:", err);
      }
    },[pmPurchaseOrderId]);

  useEffect(() => {
    fetchQualityData();
  }, [pmPurchaseOrderId, fetchQualityData]);

  const handleQualitySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${config.API_BASE_URL}/pm-quality-checks`, {
        ...qualityForm,
        pmPurchaseOrder: pmPurchaseOrderId
      });
      toast.success("Quality check entry added");
      setQualityForm({
        grnId: "",
        packingMaterial: "",
        quantity: 0,
        unit: "",
        qualityStatus: "Approved",
        qualityRemarks: "",
        rejectionReason: ""
      });
      setShowAddModal(false);
      fetchQualityData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to add quality check entry");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGRNBadge = (grnId) => {
    const grn = grns.find(g => g._id === grnId || g._id.toString() === grnId);
    return grn ? grn.grnNumber : 'Unknown GRN';
  };

  const getAvailableItems = () => {
    if (!qualityForm.grnId) return [];
    const selectedGRN = grns.find(g => g._id === qualityForm.grnId);
    if (!selectedGRN) return [];

    // Get items from the GRN or from the order
    return order?.items || [];
  };

  const columns = [
    {
      name: 'GRN',
      selector: row => getGRNBadge(row.pmGRN?._id || row.pmGRN),
      cell: row => <Badge bg="info">{getGRNBadge(row.pmGRN?._id || row.pmGRN)}</Badge>,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Packaging Material',
      selector: row => row.packingMaterial?.name,
      cell: row => (
        <div>
          <strong>{row.packingMaterial?.name || 'N/A'}</strong>
          {row.packingMaterial?.alias && (
            <div className="text-muted small">{row.packingMaterial.alias}</div>
          )}
        </div>
      ),
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Quantity',
      selector: row => `${row.quantity}`,
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'Quality Status',
      cell: row => {
        const statusMap = {
          'Approved': 'success',
          'Rejected': 'danger',
          'On Hold': 'warning',
          'Pending': 'secondary'
        };
        return (
          <Badge bg={statusMap[row.qualityStatus] || 'secondary'} className="w-100">
            {row.qualityStatus}
          </Badge>
        );
      },
      sortable: true,
      center: true,
      width: '130px',
    },
    {
      name: 'Rejection Reason',
      selector: row => row.rejectionReason,
      cell: row => row.qualityStatus === 'Rejected' && row.rejectionReason ? (
        <span className="text-danger">{row.rejectionReason}</span>
      ) : '-',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Remarks',
      selector: row => row.qualityRemarks,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Date',
      selector: row => new Date(row.createdAt).toLocaleDateString('en-IN'),
      sortable: true,
      right: true,
      width: '110px',
    },
  ];

  return (
    <div>
      <DataTable
        columns={columns}
        data={qualityEntries}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50]}
        customStyles={customStyles}
        striped
        highlightOnHover
        responsive
        subHeader
        dense
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 350px)"
        subHeaderComponent={
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            <h6 className="mb-0">Quality Check Entries</h6>
            <PermissionGate require={PERMISSIONS.QUALITY_CHECK_ADD}>
              <Button
                variant="success"
                onClick={() => setShowAddModal(true)}
                disabled={order?.status === 'Received' || order?.status === 'Fully Received' || order?.status === 'Closed' || order?.status === 'Cancelled'}
              >
                <i className="fas fa-plus me-2"></i>Add Quality Check
              </Button>
            </PermissionGate>
          </div>
        }
        noDataComponent={
          <div className="text-center py-5 text-muted">
            <i className="fas fa-clipboard-check fa-3x mb-3"></i>
            <h5>No Quality Checks</h5>
            <p>Add quality check entries to verify received materials.</p>
          </div>
        }
      />

      {canAddQuality && (
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Quality Check Entry</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleQualitySubmit}>
          <Modal.Body>
            <Row className="gy-3">
              <Col md={12}>
                <Form.Label>GRN *</Form.Label>
                <Form.Select
                  value={qualityForm.grnId}
                  onChange={(e) => setQualityForm(prev => ({...prev, grnId: e.target.value}))}
                  required
                >
                  <option value="">Select GRN</option>
                  {grns.map(grn => (
                    <option key={grn._id} value={grn._id}>
                      {grn.grnNumber} - {new Date(grn.grnDate).toLocaleDateString()}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>Packaging Material *</Form.Label>
                <Form.Select
                  value={qualityForm.packingMaterial}
                  onChange={(e) => {
                    const item = getAvailableItems().find(i => i.packingMaterial._id === e.target.value);
                    setQualityForm(prev => ({
                      ...prev,
                      packingMaterial: e.target.value,
                      unit: item?.unit || ""
                    }));
                  }}
                  required
                  disabled={!qualityForm.grnId}
                >
                  <option value="">Select Material</option>
                  {getAvailableItems().map(item => (
                    <option key={item.packingMaterial._id} value={item.packingMaterial._id}>
                      {item.packingMaterial.name}
                      {item.packingMaterial.alias && ` (${item.packingMaterial.alias})`}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>Quantity *</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={qualityForm.quantity}
                    onChange={(e) => setQualityForm(prev => ({...prev, quantity: Number(e.target.value)}))}
                    required
                  />
                  {qualityForm.unit && (
                    <span className="input-group-text">{qualityForm.unit}</span>
                  )}
                </div>
              </Col>

              <Col md={12}>
                <Form.Label>Quality Status *</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Approved"
                    name="qualityStatus"
                    checked={qualityForm.qualityStatus === 'Approved'}
                    onChange={() => setQualityForm(prev => ({...prev, qualityStatus: 'Approved', rejectionReason: ''}))}
                  />
                  <Form.Check
                    type="radio"
                    label="Rejected"
                    name="qualityStatus"
                    checked={qualityForm.qualityStatus === 'Rejected'}
                    onChange={() => setQualityForm(prev => ({...prev, qualityStatus: 'Rejected'}))}
                  />
                  <Form.Check
                    type="radio"
                    label="On Hold"
                    name="qualityStatus"
                    checked={qualityForm.qualityStatus === 'On Hold'}
                    onChange={() => setQualityForm(prev => ({...prev, qualityStatus: 'On Hold'}))}
                  />
                </div>
              </Col>

              {qualityForm.qualityStatus === 'Rejected' && (
                <Col md={12}>
                  <Form.Label>Rejection Reason *</Form.Label>
                  <Form.Control
                    value={qualityForm.rejectionReason}
                    onChange={(e) => setQualityForm(prev => ({...prev, rejectionReason: e.target.value}))}
                    placeholder="Describe the quality issue"
                    required
                  />
                </Col>
              )}

              <Col md={12}>
                <Form.Label>Quality Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={qualityForm.qualityRemarks}
                  onChange={(e) => setQualityForm(prev => ({...prev, qualityRemarks: e.target.value}))}
                  placeholder="Additional quality observations"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Entry...' : 'Add Entry'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      )}
    </div>
  );
}
